import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Prepare Control Plane and Workers',
    description: 'Connect to all three VMs (`k3s-server`, `k3s-agent-1`, `k3s-agent-2`) and confirm their hostnames and IP addresses before installing anything.',
    requirements: [
      {
        text: 'SSH into each VM using the credentials displayed in the lab dashboard'
      },
      {
        text: 'Record the private IP address of `k3s-server` (will be used by both agents)'
      },
      {
        text: 'Run an apt metadata refresh on every node (`apt-get update`)'
      },
      {
        text: 'Install the base packages required later: `ca-certificates` and `curl`'
      },
      {
        text: 'Verify outbound HTTP access from each VM (curl to a public site succeeds)'
      }
    ],
    verification: [
      'All three VMs are reachable over SSH',
      'You have each node’s private and public IP noted',
      'Package lists are refreshed without errors',
      'curl and ca-certificates are present on every node',
      'Outbound connectivity for package retrieval is confirmed'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Install the k3s Server',
    description: 'Bootstrap k3s on the control plane with the pinned version and Traefik disabled.',
    requirements: [
      {
        text: 'Export `INSTALL_K3S_VERSION="v1.28.10+k3s1"` in the shell _before_ launching the installer so the pinned release is used'
      },
      {
        text: 'Download the official installer from `https://get.k3s.io` (for example via `curl -sfL ... | sh`) and include `INSTALL_K3S_EXEC="server --disable traefik"` so the bundled ingress is skipped'
      },
      {
        text: 'Set `K3S_NODE_NAME=k3s-server` and `K3S_NODE_IP=<server-private-ip>` so the control plane advertises the correct identity and network address'
      },
      {
        text: 'Ensure the service installs as `k3s` under systemd and starts successfully'
      },
      {
        text: 'Confirm `/etc/systemd/system/k3s.service` contains the `--disable traefik` argument'
      },
      {
        text: 'Verify the command `k3s --version` reports the pinned `v1.28.x` release'
      }
    ],
    verification: [
      'k3s server service is running and enabled on `k3s-server`',
      'The installer output shows Traefik is disabled',
      'k3s version matches `v1.28.10+k3s1`',
      'Systemd unit reflects the `--disable traefik` flag',
      'No unexpected components (like Traefik pods) are deployed'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Secure Cluster Credentials',
    description: 'Protect the join token and kubeconfig files that were created on the control plane.',
    requirements: [
      {
        text: 'Locate `/var/lib/rancher/k3s/server/node-token` and restrict it to `chmod 600` with owner `root:root`'
      },
      {
        text: 'Confirm `/etc/rancher/k3s/k3s.yaml` exists after the server install'
      },
      {
        text: 'Copy `/etc/rancher/k3s/k3s.yaml` to `/root/.kube/config` for kubectl usage'
      },
      {
        text: 'Set both kubeconfigs to `chmod 600` with owner `root:root`'
      },
      {
        text: 'Export `KUBECONFIG=/etc/rancher/k3s/k3s.yaml` and run `kubectl get nodes` to confirm connectivity'
      }
    ],
    verification: [
      'Node token file exists with mode 600 and owner root:root',
      'Both kubeconfig files are restricted to mode 600',
      'kubectl commands succeed on the server using the cluster kubeconfig',
      'The `/root/.kube/config` copy is readable by root only',
      'No sensitive credential files are world-readable'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Join the Worker Nodes',
    description: 'Install the agents on both worker VMs so they register with the control plane.',
    requirements: [
      {
        text: 'Export the same `INSTALL_K3S_VERSION="v1.28.10+k3s1"` value in each agent shell session before you invoke the installer'
      },
      {
        text: 'Run the official agent installer streamed from `https://get.k3s.io`, pointing it to `https://<server-private-ip>:6443` so the workers register with the control plane'
      },
      {
        text: 'Provide the join token via `K3S_TOKEN` or the installer `--token-file` flag referencing `/var/lib/rancher/k3s/server/node-token`'
      },
      {
        text: 'Export `K3S_NODE_NAME` (matching the hostnames) and `K3S_NODE_IP` (each node’s private IP) before running the installer on every worker'
      },
      {
        text: 'Verify the `k3s-agent` systemd service is active on both `k3s-agent-1` and `k3s-agent-2`'
      },
      {
        text: 'Ensure all agents connect without TLS errors by trusting the server’s CA bundle'
      }
    ],
    verification: [
      'Both agents show `k3s-agent` service as running and enabled',
      'kubectl on the server lists `k3s-agent-1` and `k3s-agent-2`',
      'No agent install scripts report mismatched versions',
      'Server logs confirm successful node joins',
      'Each agent remains connected after a short wait'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Apply Labels and Validate Taints',
    description: 'Ensure the scheduler treats nodes correctly by labeling workers and keeping the control plane unschedulable.',
    requirements: [
      {
        text: 'On the server, label both agents with `node-role=worker` (e.g., `kubectl label node k3s-agent-1 node-role=worker`)'
      },
      {
        text: 'Confirm the labels persist on `k3s-agent-1` and `k3s-agent-2`'
      },
      {
        text: 'Check that `k3s-server` still has a `NoSchedule` taint (default control-plane taint)'
      },
      {
        text: 'Document the output of `kubectl get nodes --show-labels` for auditing'
      },
      {
        text: 'Avoid removing the control plane taint so workloads stay on the workers'
      }
    ],
    verification: [
      'Both worker nodes report the `node-role=worker` label',
      'Control plane retains a `NoSchedule` taint',
      'kubectl output clearly distinguishes the control plane and workers',
      'No workloads are scheduled on the control plane node',
      'Label and taint changes survive a systemctl restart of k3s'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Deploy the hello-k3s Workload',
    description: 'Launch a simple application and expose it through a static NodePort.',
    requirements: [
      {
        text: 'Create a deployment named `hello-k3s` with 3 replicas using a container image that serves HTTP on port 80 (e.g., `nginxdemos/hello` or `rancher/hello-world`)'
      },
      {
        text: 'Wait until all three pods report `READY 1/1` and `STATUS Running`'
      },
      {
        text: 'Expose the deployment with a service named `hello-k3s` of type `NodePort`'
      },
      {
        text: 'Set the service to forward to container port 80 and pin the nodePort to **30080**'
      },
      {
        text: 'Confirm the NodePort is listening on every node (server and both agents)'
      }
    ],
    verification: [
      '`kubectl get deploy hello-k3s` shows `3/3` ready replicas',
      '`kubectl get service hello-k3s` reports type `NodePort` with `nodePort: 30080`',
      'Each node has port 30080 listening',
      'No CrashLoopBackOff or ImagePull issues remain',
      'Service details match grader expectations exactly'
    ]
  },
  {
    id: 'task-7',
    number: 7,
    title: 'Verify Cluster Health and Reachability',
    description: 'Run final checks to ensure the cluster meets all grading requirements.',
    requirements: [
      {
        text: 'Use `kubectl get nodes` to confirm all three nodes show `Ready`'
      },
      {
        text: 'Check CoreDNS by inspecting the `coredns` deployment in `kube-system` and verifying ready replicas'
      },
      {
        text: 'From each node, curl `http://127.0.0.1:30080/` and ensure the response is HTTP 200'
      },
      {
        text: 'Run `k3s --version` on the server once more to verify the pinned version'
      },
      {
        text: 'Inspect `/var/lib/rancher/k3s/server/cred` and keep it owned by `root:root` with mode `700`'
      }
    ],
    verification: [
      'All three nodes consistently report `Ready`',
      'CoreDNS deployment shows at least one ready replica',
      'HTTP 200 responses are returned from port 30080 on server and both agents',
      'Systemd unit and k3s binary still reflect the disabled Traefik flag',
      'Credential directory permissions remain `root:root 700`'
    ]
  }
];

export function KubernetesMiniClusterInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);

    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }

    setCompletedChecks(newChecks);

    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      <div className="flex h-[1800px]">
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                  completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
                } text-[#1a1f2e]`}
              >
                {task.number}
              </div>
              <div
                className={`text-sm ${
                  index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'
                }`}
              >
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>

            {currentTask.requirements.map((requirement, reqIndex) => (
              <div
                key={reqIndex}
                className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all"
              >
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">
                    {reqIndex + 1}. {requirement.text}
                  </span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>

            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div
                  className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                    completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                  }`}
                >
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>

        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-5 py-2 bg-gradient-to-r from-[#10b981] to-[#059669] text-[#1a1f2e] rounded-md text-[13px] font-semibold cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          >
            Next Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default KubernetesMiniClusterInstructions;
